import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState, useRef, useCallback } from 'react'
import { App as CapApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { PhoneCall } from 'lucide-react'

const isNative = Capacitor.isNativePlatform()
import { authService } from './modules/identity/services/auth'
import { startSyncEngine, stopSyncEngine } from './lib/syncEngine'
import { usePushNotifications } from './lib/usePushNotifications'
import { runAmbientScan, type ScanResult, type IncomingMatch } from './modules/telephony/services/ambientScan'
import { requestRecordingPermission } from './modules/telephony/services/nativeRecording'
import {
  getCachedCallLogPermStatus,
  checkCallLogPermission,
  requestCallLogPermission,
  openAppSettings,
  type CallLogPermStatus,
} from './modules/telephony/services/callLogPermission'
import OfflineBanner from './components/OfflineBanner'
import PushToast from './components/PushToast'
import LoginPage from './modules/identity/pages/LoginPage'
import TeamPage from './modules/identity/pages/TeamPage'
import AdminPage from './modules/identity/pages/AdminPage'
import SchedulerPage from './modules/identity/pages/SchedulerPage'
import DashboardPage from './modules/reports/pages/DashboardPage'
import ReportsPage from './modules/reports/pages/ReportsPage'
import LeadsPage from './modules/leads/pages/LeadsPage'
import LeadDetailPage from './modules/leads/pages/LeadDetailPage'
import NewLeadPage from './modules/leads/pages/NewLeadPage'
import TelecallerWorkspace from './modules/workspace/pages/TelecallerWorkspace'
import WorkloadPage from './modules/workload/pages/WorkloadPage'
import FraudAlertsPage from './modules/fraud/pages/FraudAlertsPage'
import TemplatesPage from './modules/communication/pages/TemplatesPage'
import RenewalPipelinePage from './modules/policy/pages/RenewalPipelinePage'
import BillingPage from './modules/billing/pages/BillingPage'
import MySubscriptionsPage from './modules/billing/pages/MySubscriptionsPage'
import SettingsPage, { TelephonyPage } from './modules/settings/pages/SettingsPage'
import WorkingCalendarPage from './modules/settings/pages/WorkingCalendarPage'
import LeadAssignmentPage from './modules/settings/pages/LeadAssignmentPage'
import AutomationPage from './modules/automation/pages/AutomationPage'
import AuditLogPage from './modules/audit/pages/AuditLogPage'
import CompliancePage from './modules/compliance/pages/CompliancePage'
import CallHistoryPage from './modules/telephony/pages/CallHistoryPage'
import DebugLogPage from './modules/telephony/pages/DebugLogPage'
import DiscountQueuePage from './modules/discounts/pages/DiscountQueuePage'
import LeadHistoryPage from './modules/leads/pages/LeadHistoryPage'
import NegativeConfirmationsPage from './modules/negconf/pages/NegativeConfirmationsPage'
import CustomersPage from './modules/customers/pages/CustomersPage'
import CustomerDetailPage from './modules/customers/pages/CustomerDetailPage'
import Layout from './components/Layout'
import type { User } from './types'
import { UserRole } from './types'

function defaultHome(role: UserRole): string {
  return role === UserRole.BACKEND_ADMIN ? '/admin' : '/dashboard'
}

function ProtectedRoute({
  children,
  roles,
}: {
  children: React.ReactNode
  roles?: UserRole[]
}) {
  if (!authService.isAuthenticated()) {
    return <Navigate to="/login" replace />
  }
  const user = authService.getCurrentUser() as User | null
  if (roles) {
    if (!user || !roles.includes(user.role)) {
      return <Navigate to={defaultHome(user?.role as UserRole)} replace />
    }
  }
  return <>{children}</>
}

function RoleDefaultRedirect() {
  const user = authService.getCurrentUser() as User | null
  return <Navigate to={defaultHome(user?.role as UserRole)} replace />
}

type ScanBannerState = 'scanning' | 'done' | null

function ScanBanner({
  state,
  result,
  callLogAvailable,
}: {
  state: ScanBannerState
  result?: ScanResult
  callLogAvailable: boolean
}) {
  if (!state) return null
  if (state === 'scanning') {
    const label = callLogAvailable
      ? 'Scanning for new call recordings…'
      : 'Scanning for call recordings…'
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
        background: 'var(--brand-primary, #2563eb)', color: '#fff',
        fontSize: 13, padding: '6px 16px', textAlign: 'center', display: 'flex',
        alignItems: 'center', justifyContent: 'center', gap: 8,
      }}>
        <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
        {label}
      </div>
    )
  }
  if (result && result.uploaded > 0) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
        background: '#16a34a', color: '#fff',
        fontSize: 13, padding: '6px 16px', textAlign: 'center',
      }}>
        {result.uploaded} new recording{result.uploaded > 1 ? 's' : ''} uploaded
      </div>
    )
  }
  return null
}

function CallLogPermissionModal({
  status,
  onAllow,
  onSkip,
  onOpenSettings,
}: {
  status: CallLogPermStatus
  onAllow: () => void
  onSkip: () => void
  onOpenSettings: () => void
}) {
  if (status !== 'not_asked' && status !== 'permanently_denied') return null
  const isPermanent = status === 'permanently_denied'
  const itemStyle = { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 10 }
  const labelStyle = { color: 'var(--text-primary)', fontWeight: 600 }
  return (
    <div className="modal-overlay" style={{ zIndex: 10000 }} role="dialog" aria-modal="true" aria-labelledby="call-log-disclosure-title">
      <div className="modal-panel" style={{ maxWidth: 380, padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: 'var(--brand-primary)' }}>
          <PhoneCall size={36} aria-hidden="true" />
        </div>
        <h3 id="call-log-disclosure-title" className="modal-header" style={{ marginBottom: 12, textAlign: 'center' }}>
          {isPermanent ? 'Call log access blocked' : 'Call log and recording access'}
        </h3>
        {isPermanent ? (
          <>
            <p style={{ ...itemStyle, textAlign: 'center', marginBottom: 20 }}>
              Call log access is turned off. To match calls to leads automatically, open the app settings and allow the
              Call log permission.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button className="btn-primary" onClick={onOpenSettings}>Open settings</button>
              <button className="btn-secondary" onClick={onSkip}>Continue without it</button>
            </div>
          </>
        ) : (
          <>
            <p style={{ ...itemStyle, marginBottom: 14 }}>
              Insurance Khareedo would like to read your phone's call history and call recordings. Please review how they are used.
            </p>
            <p style={itemStyle}>
              <span style={labelStyle}>What we access: </span>
              the number, time, duration and direction of calls in your call log, and call recordings saved on your phone.
            </p>
            <p style={itemStyle}>
              <span style={labelStyle}>Why: </span>
              to match each call to the right customer lead and attach the recording, so your supervisor can review follow-ups.
            </p>
            <p style={itemStyle}>
              <span style={labelStyle}>What is uploaded: </span>
              only calls that match one of your dealership's leads. Other calls, such as personal calls, are skipped and never uploaded.
            </p>
            <p style={{ ...itemStyle, marginBottom: 20 }}>
              <span style={labelStyle}>Your choice: </span>
              this data is never sold or used for advertising. You can decline, and change this any time in your phone's settings.
              Without it, calls must be logged by hand.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button className="btn-primary" onClick={onAllow}>Agree and continue</button>
              <button className="btn-secondary" onClick={onSkip}>No thanks</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function IncomingCallToasts({
  matches,
  onDismiss,
}: {
  matches: IncomingMatch[]
  onDismiss: (leadId: number) => void
}) {
  const navigate = useNavigate()
  if (matches.length === 0) return null
  return (
    <div style={{
      position: 'fixed', bottom: 80, right: 16, zIndex: 9998,
      display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320,
    }}>
      {matches.map(m => (
        <div key={m.lead_id} style={{
          background: 'var(--surface-card, #fff)', border: '1px solid var(--border, #e5e7eb)',
          borderRadius: 10, padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
            📞 Incoming call recorded
          </p>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)' }}>
            {m.customer_name} — recording uploaded &amp; analysis queued
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button
              style={{
                fontSize: 12, padding: '3px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                background: 'var(--brand-primary, #2563eb)', color: '#fff', fontWeight: 500,
              }}
              onClick={() => { navigate(`/leads/${m.lead_id}`); onDismiss(m.lead_id) }}
            >
              View Lead
            </button>
            <button
              style={{
                fontSize: 12, padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
                background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-secondary)',
              }}
              onClick={() => onDismiss(m.lead_id)}
            >
              Dismiss
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

function App() {
  const [scanBanner, setScanBanner] = useState<ScanBannerState>(null)
  const [scanResult, setScanResult] = useState<ScanResult | undefined>(undefined)
  const [incomingToasts, setIncomingToasts] = useState<IncomingMatch[]>([])
  const [callLogPermStatus, setCallLogPermStatus] = useState<CallLogPermStatus>(() =>
    getCachedCallLogPermStatus()
  )
  const [showPermModal, setShowPermModal] = useState(false)
  // Pending scan to run after permission decision
  const pendingScanRef = useRef(false)
  const bannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const dismissIncoming = useCallback((leadId: number) => {
    setIncomingToasts(prev => prev.filter(m => m.lead_id !== leadId))
  }, [])

  const handleScanStatus = useCallback((status: 'started' | 'done', result?: ScanResult) => {
    if (status === 'started') {
      setScanBanner('scanning')
    } else {
      setScanResult(result)
      setScanBanner(result && result.uploaded > 0 ? 'done' : null)
      if (result && result.uploaded > 0) {
        if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current)
        bannerTimerRef.current = setTimeout(() => setScanBanner(null), 4000)
      }
      if (result?.incomingMatches?.length) {
        setIncomingToasts(prev => {
          const existing = new Set(prev.map(m => m.lead_id))
          const newOnes = result.incomingMatches.filter(m => !existing.has(m.lead_id))
          return [...prev, ...newOnes]
        })
      }
    }
  }, [])

  const triggerScan = useCallback(() => {
    if (!isNative) return
    if (!authService.isAuthenticated()) return
    const perm = getCachedCallLogPermStatus()
    if (perm === 'not_asked') {
      // Show rationale first; scan will run after user decides
      pendingScanRef.current = true
      setShowPermModal(true)
      return
    }
    setCallLogPermStatus(perm)
    runAmbientScan(handleScanStatus)
  }, [handleScanStatus])

  const handlePermAllow = useCallback(async () => {
    setShowPermModal(false)
    // Request READ_CALL_LOG (ambient scan) + READ_MEDIA_AUDIO (recording upload) together
    const [result] = await Promise.all([
      requestCallLogPermission(),
      requestRecordingPermission(),   // READ_CALL_LOG + READ_MEDIA_AUDIO via CallLogPlugin
    ])
    setCallLogPermStatus(result)
    if (pendingScanRef.current) {
      pendingScanRef.current = false
      runAmbientScan(handleScanStatus)
    }
  }, [handleScanStatus])

  const handlePermSkip = useCallback(() => {
    setShowPermModal(false)
    // Mark as denied so we don't show the modal again
    // (user said "not now" — we treat as denied, not permanently)
    try { localStorage.setItem('oem_call_log_perm', 'denied') } catch { /* ignore */ }
    setCallLogPermStatus('denied')
    if (pendingScanRef.current) {
      pendingScanRef.current = false
      runAmbientScan(handleScanStatus)
    }
  }, [handleScanStatus])

  const handlePermOpenSettings = useCallback(() => {
    openAppSettings()
    setShowPermModal(false)
  }, [])

  useEffect(() => {
    if (!isNative) return
    // Sync the cached status with the OS (may have changed in Settings). Never request here:
    // the OS prompt must only appear after the in-app disclosure is accepted.
    checkCallLogPermission().then(s => {
      if (s !== 'not_asked') setCallLogPermStatus(s)
    })
  }, [])

  // Show the disclosure once the user is signed in and has not yet decided.
  const location = useLocation()
  useEffect(() => {
    if (!isNative || showPermModal) return
    if (!authService.isAuthenticated()) return
    if (getCachedCallLogPermStatus() !== 'not_asked') return
    checkCallLogPermission().then(s => {
      if (s === 'not_asked') {
        pendingScanRef.current = true
        setShowPermModal(true)
      } else {
        setCallLogPermStatus(s)
      }
    })
  }, [location.pathname, showPermModal])

  useEffect(() => {
    startSyncEngine()
    return () => stopSyncEngine()
  }, [])

  useEffect(() => {
    let listenerHandle: { remove: () => void } | null = null
    CapApp.addListener('appStateChange', state => {
      if (state.isActive) {
        // Re-check permission each time (user may have changed it in Settings)
        checkCallLogPermission().then(s => {
          if (s !== 'not_asked') setCallLogPermStatus(s)
        })
        triggerScan()
      }
    }).then(handle => { listenerHandle = handle })
    return () => { listenerHandle?.remove() }
  }, [triggerScan])

  usePushNotifications()

  const callLogAvailable = callLogPermStatus === 'granted'

  return (
    <>
      <ScanBanner state={scanBanner} result={scanResult} callLogAvailable={callLogAvailable} />
      <IncomingCallToasts matches={incomingToasts} onDismiss={dismissIncoming} />
      {isNative && showPermModal && (
        <CallLogPermissionModal
          status={callLogPermStatus}
          onAllow={handlePermAllow}
          onSkip={handlePermSkip}
          onOpenSettings={handlePermOpenSettings}
        />
      )}
      <OfflineBanner />
      <PushToast />
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<RoleDefaultRedirect />} />
        <Route path="dashboard" element={<DashboardPage />} />

        {/* Lead management */}
        <Route path="leads" element={<LeadsPage />} />
        <Route path="leads/history" element={
          <ProtectedRoute roles={[UserRole.TELECALLER, UserRole.TEAM_LEADER, UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL]}>
            <LeadHistoryPage />
          </ProtectedRoute>
        } />
        <Route path="leads/new" element={<NewLeadPage />} />
        <Route path="leads/:id" element={<LeadDetailPage />} />

        {/* Customer profiles — IH, DP, BA */}
        <Route
          path="customers"
          element={
            <ProtectedRoute roles={[UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL, UserRole.BACKEND_ADMIN]}>
              <CustomersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="customers/:id"
          element={
            <ProtectedRoute roles={[UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL, UserRole.BACKEND_ADMIN]}>
              <CustomerDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Telecaller workspace */}
        <Route path="workspace" element={
          <ProtectedRoute roles={[UserRole.TELECALLER, UserRole.TEAM_LEADER]}>
            <TelecallerWorkspace />
          </ProtectedRoute>
        } />

        {/* Management */}
        <Route path="workload" element={
          <ProtectedRoute roles={[UserRole.TEAM_LEADER, UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL]}>
            <WorkloadPage />
          </ProtectedRoute>
        } />
        <Route path="team" element={<TeamPage />} />
        <Route
          path="reports"
          element={
            <ProtectedRoute roles={[
              UserRole.INSURANCE_HEAD,
              UserRole.TEAM_LEADER,
              UserRole.DEALER_PRINCIPAL,
            ]}>
              <ReportsPage />
            </ProtectedRoute>
          }
        />

        {/* Fraud control — IH and DP */}
        <Route
          path="fraud"
          element={
            <ProtectedRoute roles={[
              UserRole.INSURANCE_HEAD,
              UserRole.DEALER_PRINCIPAL,
            ]}>
              <FraudAlertsPage />
            </ProtectedRoute>
          }
        />

        {/* Policy — renewal pipeline */}
        <Route path="policy/renewal-pipeline" element={
          <ProtectedRoute roles={[UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL, UserRole.TEAM_LEADER]}>
            <RenewalPipelinePage />
          </ProtectedRoute>
        } />

        {/* Communication — templates managed by BACKEND_ADMIN */}
        <Route
          path="communication/templates"
          element={
            <ProtectedRoute roles={[UserRole.BACKEND_ADMIN]}>
              <TemplatesPage />
            </ProtectedRoute>
          }
        />

        {/* Billing — BA manages, DP self-service */}
        <Route
          path="billing"
          element={
            <ProtectedRoute roles={[UserRole.BACKEND_ADMIN]}>
              <BillingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="billing/my-subscriptions"
          element={
            <ProtectedRoute roles={[UserRole.DEALER_PRINCIPAL]}>
              <MySubscriptionsPage />
            </ProtectedRoute>
          }
        />

        {/* Telephony — call history + reminders for telecallers */}
        <Route path="calls/history" element={<CallHistoryPage />} />
        <Route path="debug-log" element={<DebugLogPage />} />

        {/* Audit — DP sees own tenant's operational trail; IH and BA additionally */}
        <Route
          path="audit"
          element={
            <ProtectedRoute roles={[UserRole.DEALER_PRINCIPAL, UserRole.INSURANCE_HEAD, UserRole.BACKEND_ADMIN]}>
              <AuditLogPage />
            </ProtectedRoute>
          }
        />

        {/* DPDP Compliance — data controller is the dealership (DP/IH); TC and TL can manage consent only */}
        <Route
          path="compliance"
          element={
            <ProtectedRoute roles={[UserRole.DEALER_PRINCIPAL, UserRole.INSURANCE_HEAD, UserRole.TEAM_LEADER, UserRole.TELECALLER]}>
              <CompliancePage />
            </ProtectedRoute>
          }
        />

        {/* Discount approval queue — TL, IH, DP */}
        <Route
          path="discounts"
          element={
            <ProtectedRoute roles={[UserRole.TEAM_LEADER, UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL]}>
              <DiscountQueuePage />
            </ProtectedRoute>
          }
        />

        {/* Negative confirmations — IH, DP */}
        <Route
          path="negative-confirmations"
          element={
            <ProtectedRoute roles={[UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL]}>
              <NegativeConfirmationsPage />
            </ProtectedRoute>
          }
        />

        {/* Old path — kept as a redirect so bookmarks/links don't break */}
        <Route path="settings" element={<Navigate to="/settings/discounts" replace />} />

        {/* Dealer Principal settings (discount limits config) */}
        <Route
          path="settings/discounts"
          element={
            <ProtectedRoute roles={[UserRole.DEALER_PRINCIPAL]}>
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        {/* Dealer Principal settings (telephony provider config) */}
        <Route
          path="settings/telephony"
          element={
            <ProtectedRoute roles={[UserRole.DEALER_PRINCIPAL]}>
              <TelephonyPage />
            </ProtectedRoute>
          }
        />

        {/* Old path — kept as a redirect so bookmarks/links don't break */}
        <Route path="automation" element={<Navigate to="/settings/whatsapp" replace />} />

        {/* WhatsApp Automation — IH and DP */}
        <Route
          path="settings/whatsapp"
          element={
            <ProtectedRoute roles={[UserRole.DEALER_PRINCIPAL, UserRole.INSURANCE_HEAD]}>
              <AutomationPage />
            </ProtectedRoute>
          }
        />

        {/* Working calendar (working days + holidays) — IH and DP */}
        <Route
          path="settings/working-calendar"
          element={
            <ProtectedRoute roles={[UserRole.DEALER_PRINCIPAL, UserRole.INSURANCE_HEAD]}>
              <WorkingCalendarPage />
            </ProtectedRoute>
          }
        />

        {/* Lead assignment roles — DP only */}
        <Route
          path="settings/lead-assignment"
          element={
            <ProtectedRoute roles={[UserRole.DEALER_PRINCIPAL]}>
              <LeadAssignmentPage />
            </ProtectedRoute>
          }
        />

        {/* Backend admin only */}
        <Route
          path="admin"
          element={
            <ProtectedRoute roles={[UserRole.BACKEND_ADMIN]}>
              <AdminPage />
            </ProtectedRoute>
          }
        />

        {/* Scheduler — BA and IH */}
        <Route
          path="settings/scheduler"
          element={
            <ProtectedRoute roles={[UserRole.BACKEND_ADMIN, UserRole.INSURANCE_HEAD]}>
              <SchedulerPage />
            </ProtectedRoute>
          }
        />
      </Route>
    </Routes>
    </>
  )
}

export default App
